import { LocalStorageService } from '@df/browser/storage/local-storage.service';
import { TNumberPerSize } from '@df/core/df-core.interfaces';
import { Client } from '@df/core/service/client';
import { Subject, Subscription } from 'rxjs';

export interface IListingSizeOption {
    size: number; // 3 - items in row
    label?: string;
    onSize?: string[]; // [`X`, `L`, `M` , `S`]
    active?: boolean; // true
    default?: boolean; // true
}

export class ListingSize {
    /**
     * Subject for option change observations
     */
    private subject = new Subject<IListingSizeOption>();

    /**
     * Size selection id
     */
    private readonly storageId = 'listingSize';

    /**
     * Subscriptions
     */
    private subsriptions = new Subscription();

    /**
     * Size options for current viewport size
     */
    options: IListingSizeOption[] = [];

    /**
     * Current selection
     */
    selection?: IListingSizeOption;

    /**
     * Currently selected size
     */
    currentSize: TNumberPerSize = [0, 0, 0, 0];

    /**
     * Actual list size
     */
    private size = 0;

    constructor(
        private client: Client,
        private localStorageService: LocalStorageService,
        private allOptions: IListingSizeOption[],
        public defaultSize?: TNumberPerSize
    ) {
        this.readStorage();
        this.syncOptions(true);
        this.subsriptions.add(this.client.obs.breakpoint.subscribe(() => this.syncOptions()));
    }

    ///////////////////////////
    ///
    /// INTEGRATION
    ///
    ///////////////////////////

    /**
     * Gets list of size options
     */
    getOptions() {
        return this.options;
    }

    /**
     * Registers callback triggered when sort option changes
     */
    subscribe(next: (value: IListingSizeOption) => void) {
        return this.subject.subscribe(next);
    }

    /**
     * Triggers all registered callbacks
     */
    next() {
        this.subject.next(this.getSelection()!);
    }

    /**
     * Sets size options
     */
    setSizeOptions(options: IListingSizeOption[]) {
        this.allOptions = options;
        this.syncOptions();
    }

    /**
     * Sets default size selection
     */
    setDefaultSize(defaultSize: TNumberPerSize) {
        this.defaultSize = defaultSize;
        this.apply();
    }

    ///////////////////////////
    ///
    /// SELECTION
    ///
    ///////////////////////////

    /**
     * Syncs options with size
     */
    private syncOptions(silent = false) {
        const sizeName = this.client.size;
        this.options = this.allOptions.filter(i => (i.onSize ? i.onSize.includes(sizeName) : true));
        this.apply(silent);
    }

    /**
     * Sets option for current client size
     */
    private setSize(option: number | IListingSizeOption) {
        const sizeOptionValue = this.toListingSizeOption(option)?.size || 0;
        const clientSizeId = this.client.sizeId;
        this.currentSize[clientSizeId] = sizeOptionValue;
        this.apply();
        this.saveStorage();
    }

    /**
     * Retrieves current size
     */
    private getSize(): number {
        const clientSizeId = this.client.sizeId;
        const option =
            this.toListingSizeOption(this.currentSize[clientSizeId]) ||
            (this.defaultSize && this.toListingSizeOption(this.defaultSize[clientSizeId])) ||
            this.options[0];
        return option.size;
    }

    /**
     * Applies options and its selections
     */
    private apply(silent = false) {
        const size = this.getSize();
        if (this.size !== size) {
            this.size = size;
            if (!silent) {
                this.next();
            }
        }
    }

    /**
     * Retrieves currently selected option
     */
    getSelection(): IListingSizeOption | undefined {
        return this.toListingSizeOption(this.size);
    }

    /**
     * Sets its own data from source
     */
    setState(source: number): void {
        this.setSize(source);
    }

    /**
     * Gets own data
     */
    getState(defaultSize: number = 4): number {
        return this.size || defaultSize;
    }

    /**
     * If options is selected test
     */
    isSelected(option: number | IListingSizeOption): boolean {
        const size = typeof option === 'number' ? option : option.size;
        return size === this.size;
    }

    ///////////////////////////
    ///
    /// STORAGE
    ///
    ///////////////////////////

    /**
     * Saves data to storage
     */
    private saveStorage(): void {
        this.localStorageService.set(this.storageId, this.currentSize);
    }

    /**
     * Reads data from storage
     */
    private readStorage(): void {
        const storageValue = this.localStorageService.get(this.storageId);
        if (Array.isArray(storageValue) && storageValue.length === 4) {
            this.currentSize = storageValue.map(i => Number(i) || 0) as TNumberPerSize;
        }
    }

    ///////////////////////////
    ///
    /// HELPERS
    ///
    ///////////////////////////

    /**
     * Size option tracking method
     */
    optionTrackFn(index: number, option: IListingSizeOption) {
        return option.size;
    }

    /**
     * Converts input to size option
     */
    private toListingSizeOption(option: number | IListingSizeOption): IListingSizeOption | undefined {
        const size = typeof option === 'number' ? option : option.size;
        return this.options.find(option => option.size == size);
    }

    /**
     * Cleanup method called on deostry
     */
    clearSubscriptions() {
        this.subsriptions.unsubscribe();
    }

    get selectedSize(): number {
        return this.size;
    }

    ///////////////////////////
    ///
    /// LEGACY SUPPORT
    ///
    ///////////////////////////

    // /**
    //  * Returns currently selected selection label
    //  * @return {string}
    //  * @deprecated
    //  */
    // get selectionLabel(): string {
    //     const selection = this.getSelection();
    //     return selection ? `${selection.label || selection.size}` : '';
    // }
}
